import React from 'react';
import { useBalance } from 'wagmi';
import { CopyAddress } from '@components/CopyAddress';
import { Button } from '@components/main-components/button/Button';
import { Modal } from '@components/main-components/modals/Modal';
import { useAuth } from '@context/auth/base';
import { ModalContext } from '@context/modal';
import { useCopy } from '@hooks/useCopy';
import { formatWeiToEthForDisplay, humanReadableChainName } from '@utils/helpers';

export const InsufficientFundsModal = ({
  description,
  chainId,
  primaryButton,
  secondaryButton,
}: {
  description: React.ReactNode;
  chainId: number;
  primaryButton: React.ReactNode;
  secondaryButton: React.ReactNode;
}) => {
  const { closeCancelModal } = ModalContext.useContainer();
  const { authUserAddress } = useAuth();
  const { copy } = useCopy({ text: authUserAddress, successMessage: 'Address Copied' });

  const {
    data: balanceData,
    isError: balanceError,
    isLoading: balanceLoading,
  } = useBalance({
    address: authUserAddress || '0x000',
    enabled: !!authUserAddress,
    formatUnits: 'gwei',
    chainId,
    staleTime: 0,
  });

  const body = (
    <div className="flex w-full flex-col items-center gap-y-6">
      <p className="font-base text-base-m">{description}</p>

      <div className="flex w-full flex-col items-center gap-y-4 font-base">
        <div className="flex w-full items-start justify-between align-top">
          <div className="flex flex-col justify-between gap-y-0.5">
            <h2 className="text-base-l font-medium">Your Balance</h2>
            <p className="text-base-s text-base500">{humanReadableChainName(chainId)}</p>
          </div>
          <div className="flex flex-col justify-between gap-y-0.5 ">
            <div className="font-title text-title-s font-medium ">
              {balanceLoading || balanceError ? (
                <Button variant="text" size="XS" label="" loading />
              ) : balanceData ? (
                <>
                  {formatWeiToEthForDisplay({
                    weiValue: balanceData?.value.toString(),
                    maxDecimals: 4,
                  })}{' '}
                  ETH
                </>
              ) : (
                <p className="text-base-s text-base500">Unable to load</p>
              )}
            </div>
            {authUserAddress && (
              <p className="text-right text-base-s text-base500">
                <CopyAddress publicAddress={authUserAddress.toLowerCase()} />
              </p>
            )}
          </div>
        </div>
        {authUserAddress && (
          <div className="w-[100%]">
            <p className="text-base-m text-base800">Transfer funds to your wallet below:</p>
            <div className="my-2 flex w-[100%]">
              <input
                className="mr-2 h-10 w-full gap-1 rounded-[6px] px-3 py-1 text-base-l text-base500"
                value={authUserAddress}
                disabled
              />
              <Button
                variant="primary"
                label="Copy"
                size="S"
                onClick={async e => {
                  e.preventDefault();
                  copy();
                }}
              />
            </div>
            {(!!primaryButton || !!secondaryButton) && (
              <p className="mt-4 flex justify-center text-base-m font-medium text-base800">or</p>
            )}
          </div>
        )}
        {(!!primaryButton || !!secondaryButton) && (
          <div className="flex w-full flex-col gap-y-3">
            {primaryButton}
            {secondaryButton}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <Modal
      header={<div className="flex items-center">Insufficient Funds</div>}
      body={body}
      closeModal={closeCancelModal}
    />
  );
};
